import { useQuery } from '@tanstack/react-query'
import { QUERY_KEYS } from 'constants/constants'
import { NODE_ENV } from 'helpers'
import { useCallback, useEffect, useMemo } from 'react'
import { JSONTree } from 'react-json-tree'
import { Navigate } from 'react-router-dom'
import { TranslationContextProvider } from '../../../components/discover/aivy-pdf-reports/context/general-translations'
import {
  useLanguageContext,
  usePartnerContext,
  useUserContext
} from '../../../context'
import { query as graphqlQuery } from '../../../graphql'
import { skipsOnboarding } from '../../../helpers/experiment'
import { useCareerQuery } from '../../../hooks/useCareerQuery'
import {
  LIFE_CYCLE_STAGES,
  useLifecycleStageTracking
} from '../../../hooks/useLifecycleStageTracking'
import { useMatchingProfileQuery } from '../../../hooks/useMatchingProfileQuery'
import { useMatchingQuery } from '../../../hooks/useMatchingQuery'
import { useRequirementProfileQuery } from '../../../hooks/useRequirementProfileQuery'
import { useTalentProfileQuery } from '../../../hooks/useTalentProfileQuery'
import { useTalentQuery } from '../../../hooks/useTalentQuery'
import { useTeamProfileQuery } from '../../../hooks/useTeamProfileQuery'
import { useTalentStorage } from '../../pricing/hooks'
import AddCareerModal from '../components/AddCareerModal'
import AssessmentPreview from '../components/AssessmentPreview'
import AssistantDrawer from '../components/AssistantDrawer'
import ConditionalFlexibleMatchingSection from '../components/ConditionalFlexibleMatchingSection'
import ConditionalTalentMatchSection from '../components/ConditionalTalentMatchSection'
import DiscoverPageLayout from '../components/DiscoverPageLayout'
import EvaluationSection from '../components/EvaluationSection'
import InviteTalentModal from '../components/InviteTalentModal'
import MatchOverviewSection from '../components/MatchOverviewSection'
import SelectionOverviewSection from '../components/SelectionOverviewSection'
import ConditionalRequirementProfileSection from '../components/requirementProfile/ConditionalRequirementProfileSection'
import ConditionalTalentProfileSection from '../components/talentProfile/ConditionalTalentProfileSection'
import ConditionalTeamProfileSection from '../components/teamProfile/ConditionalTeamProfileSection'
import { DiscoverContextProvider } from '../context/DiscoverContext'
import { useSelectionParams } from '../hooks/useSelectionParams'
import { showDemoAlert } from '../utils'
import {
  canLoadTalentResults,
  discoverTalentLock,
  splitTalentSelection
} from '../utils/talent-storage-lock'

const PLG_LEAD_PROGRESS = Number((1 / 6).toFixed(2))

export default function Discover() {
  const { language } = useLanguageContext()
  const { trackLifecycleStage } = useLifecycleStageTracking()
  const { partner } = usePartnerContext()
  const isDiscoverVisible = partner.isDashboardComponentVisible.discover

  const { talents: selectedTalentIds, careers: selectedCareerIds } =
    useSelectionParams()

  // Get first selected IDs for current implementation
  const careerId = selectedCareerIds[0]
  const talentId = selectedTalentIds[0]

  const isTalentQueryEnabled = Boolean(talentId)

  const talentStorage = useTalentStorage({ fetch: 'auto' })
  const teamSelection = useMemo(
    () => splitTalentSelection(selectedTalentIds, talentStorage),
    [selectedTalentIds, talentStorage]
  )

  const {
    activeSpace,
    cognitoUser: { username }
  } = useUserContext()
  const hasCompletedOnboarding = activeSpace?.hasCompletedOnboarding
  // Variante B: kein erzwungenes Onboarding-Skript, auch wenn der Partner über
  // die Navigation doch auf /discover landet — sonst würde B den Assistant
  // nachträglich doch bekommen und die Varianten würden verschmieren.
  const skipOnboarding = skipsOnboarding(partner?.id, hasCompletedOnboarding)

  const { data: career, isLoading: isLoadingCareer } = useCareerQuery(careerId)

  const { data: talent, isLoading: isLoadingTalent } = useTalentQuery(
    talentId,
    username,
    { enabled: isTalentQueryEnabled }
  )

  // Talentspeicher. Für ein gesperrtes Talent laufen keine Ergebnis-Queries,
  // die Abschnitte zeigen dann eine gesperrte Karte. Solange nicht gezählt
  // ist (pending), ebenfalls nicht, damit keine echten Ergebnisse aufblitzen.
  // Ergebnisse laden am owner, deshalb zählt auch die Sperre der Person, etwa
  // für einen Space, der selbst nicht zählt. In legacy und preview ist nichts
  // gesperrt.
  const talentLock = discoverTalentLock(talentId, talentStorage, talent?.owner)
  const canLoadResults = canLoadTalentResults(talentLock)

  const isMatchingEnabled = useMemo(
    () => Boolean(career && talent) && canLoadResults,
    [career, talent, canLoadResults]
  )

  const trackPlgCreated = useCallback(() => {
    if (typeof window === 'undefined') return

    const storageKey = 'plgCreatedTracked'
    if (localStorage.getItem(storageKey) === 'true') return

    trackLifecycleStage({
      lifecycle_stage: LIFE_CYCLE_STAGES.PLG_CREATED,
      lifecycle_stage_before: '',
      aivy_form_id: partner.aivy_form_id,
      lead_progress: PLG_LEAD_PROGRESS,
      partner_id: partner.id
    })

    localStorage.setItem(storageKey, 'true')
  }, [trackLifecycleStage, partner.aivy_form_id, partner.id])

  useEffect(() => {
    trackPlgCreated()
  }, [trackPlgCreated])

  const { data: requirementProfile, isLoading: isLoadingRequirementProfile } =
    useRequirementProfileQuery({ careerId, language })

  // Ohne Sperren sind unlockedIds genau selectedTalentIds
  const { data: teamProfileData, isLoading: isLoadingTeamProfile } =
    useTeamProfileQuery({
      spaceIds: teamSelection.unlockedIds,
      careerId,
      language,
      enabled: teamSelection.unlockedIds.length > 1
    })

  const { data: talentProfileData, isLoading: isLoadingTalentProfile } =
    useTalentProfileQuery({
      spaceId: talentId,
      ownerId: talent?.owner,
      language,
      enabled: !!talent && canLoadResults
    })

  const { data: matchingData, isLoading: isLoadingMatching } = useMatchingQuery(
    career?.id,
    talent?.owner,
    {
      enabled: isMatchingEnabled
    }
  )

  // Eine abgeschaltete Query liefert weiter Daten aus dem (persistierten)
  // Cache. Für gesperrte Talente deshalb nichts durchreichen.
  const teamProfile = teamSelection.pending ? undefined : teamProfileData
  const talentProfile = canLoadResults ? talentProfileData : undefined
  const matching = canLoadResults ? matchingData : undefined

  // Get complete_dataset from partner_id variant (proven to include it)
  // Uses same query key as careers-matching-container.js for cache sharing
  const { data: isDatasetCompleteData } = useQuery({
    queryKey: QUERY_KEYS.matchMeIfYouCanByOwnerAndPartner(
      talent?.owner,
      partner.id
    ),
    queryFn: () =>
      graphqlQuery({
        query: 'matchMeIfYouCan',
        variables: { partner_id: partner.id, user_id: talent?.owner }
      }),
    select: (response) => {
      const careers = JSON.parse(response)
      const careerMatch = careers.find((c) => c.id === career?.id)
      return careerMatch ? careerMatch.complete_dataset : null
    },
    enabled: isMatchingEnabled,
    refetchOnWindowFocus: false
  })

  const isDatasetComplete = canLoadResults ? isDatasetCompleteData : undefined

  const isMatchingProfileEnabled = useMemo(
    () => Boolean(career && talent && matching) && canLoadResults,
    [career, talent, matching, canLoadResults]
  )

  const { data: matchingProfileData, isLoading: isLoadingProfile } =
    useMatchingProfileQuery(
      talent?.id,
      talent?.owner,
      career?.id,
      language,
      matching,
      { enabled: isMatchingProfileEnabled }
    )
  const matchingProfile = canLoadResults ? matchingProfileData : undefined

  const isLoading =
    isLoadingCareer || isLoadingTalent || isLoadingMatching || isLoadingProfile

  if (!isDiscoverVisible) {
    return <Navigate to='/404' replace />
  }

  return (
    // key: überspringt ein Admin das Onboarding, startet der Discover-State
    // neu. Sonst feuern laufende Assistant-Sequenzen (Timeouts) in den neuen
    // Zustand nach und Fokus-Modus samt Backdrop bleiben stehen. Bewusst nicht
    // an hasCompletedOnboarding gekoppelt, sonst ginge beim echten Abschluss
    // der Joyride „Dashboard freigeschaltet" verloren.
    <DiscoverContextProvider
      key={activeSpace?.onboardingSkippedByAdmin ? 'admin-skip' : 'default'}
    >
      {/* <RegistrationAssistantHandler /> */}
      <AssessmentPreview />
      <div className='flex flex-row'>
        <div className='flex w-full flex-1'>
          <DiscoverPageLayout>
            {(NODE_ENV === 'develop' || NODE_ENV === 'development') && (
              <JSONTree
                data={{
                  requirementProfile,
                  talentProfile,
                  teamProfile,
                  matching,
                  matchingProfile
                }}
              />
            )}
            {(selectedTalentIds.length >= 1 ||
              selectedCareerIds.length >= 1) && (
              <SelectionOverviewSection
                selectedTalents={selectedTalentIds}
                selectedCareers={selectedCareerIds}
                className={'mb-6'}
              />
            )}

            <MatchOverviewSection
              variant={
                hasCompletedOnboarding || skipOnboarding
                  ? 'default'
                  : 'onboarding'
              }
              career={career}
              talent={talent}
              isLoadingCareer={isLoadingCareer}
              className={'mb-6'}
              matchingProfile={matchingProfile}
              disableRender={
                selectedTalentIds.length > 0 || selectedCareerIds.length > 0
              }
            />

            <ConditionalFlexibleMatchingSection
              careers={selectedCareerIds}
              talents={selectedTalentIds}
              career={career}
              talent={talent}
            />
            <ConditionalRequirementProfileSection
              career={career}
              talent={talent}
              requirementProfile={requirementProfile}
              isLoading={isLoadingRequirementProfile}
            />
            <ConditionalTalentProfileSection
              talent={talent}
              career={career}
              talentProfile={talentProfile}
              isLoading={isLoadingTalentProfile}
              resultLock={talentLock}
            />
            <ConditionalTeamProfileSection
              teamProfile={teamProfile}
              career={career}
              selectedCareerIds={selectedCareerIds}
              selectedTalentIds={selectedTalentIds}
              isLoading={isLoadingTeamProfile}
              isLoadingCareer={isLoadingCareer}
              lockedCount={teamSelection.lockedIds.length}
              isStoragePending={teamSelection.pending}
            />

            <ConditionalTalentMatchSection
              career={career}
              talent={talent}
              matching={matching}
              matchingProfile={matchingProfile}
              isLoading={isLoading}
              isDatasetComplete={isDatasetComplete}
              resultLock={talentLock}
            />

            {!hasCompletedOnboarding && (!career || !talent) && (
              <EvaluationSection
                className={'mt-6'}
                career={career}
                talent={talent}
                onPressCreateCareer={() => showDemoAlert()}
                onPressCreateTalent={() => showDemoAlert()}
              />
            )}

            {/* <AssessmentPreviewModal /> */}
            <InviteTalentModal />
            <AddCareerModal />
          </DiscoverPageLayout>
        </div>
        {
          <TranslationContextProvider data={matchingProfile}>
            <AssistantDrawer />
            {/* <AssistantSpotlightController /> */}
          </TranslationContextProvider>
        }
      </div>
    </DiscoverContextProvider>
  )
}
